import {
  Supertype,
  supertypeClass,
  property,
  remote,
  Remoteable,
  Persistable,
  amorphicStatic,
} from '@haventech/amorphic';
import {
  IsomorphicPersistent,
  Persistent,
  IsomorphicQuery,
  Query,
  Isomorphic,
  Serializable,
  AppController,
  SubController,
  Everything,
} from '@hit/amorphic-types';
import { emailConfig } from '../classes/EmailConfig';
import { Controller } from '../Controller';
import { EmailTemplate } from '../interfaces/EmailTemplateInterface';
import { Utils } from '../../../../common/js/Utils';
import { HitAppCtx } from '../../../../common/js/orca/HitAppCtx';

import { LoggerSingleton, Category, PiiLevelEnum } from '../../../../common/js/logger/LoggerSingleton';

@supertypeClass({ toClient: true, toServer: true })
export class EmailNotificationController extends AppController {
  @property({ getType: () => Controller })
  controller: Controller;

  @property({ type: Array })
  emailTemplates: Array<EmailTemplate> = [];

  constructor() {
    super();
  }

  static new(parentController) {
    return new EmailNotificationController().init(parentController);
  }

  init(parentController) {
    this.controller = parentController;
    return this;
  }

  private initializeEmailTemplates(): void {
    Object.keys(emailConfig).forEach((config) => {
      this.emailTemplates.push(<EmailTemplate>emailConfig[config]);
    });
  }

  private createInfoTable(currentEnvironment: string): string {
    const customer = this.controller.customer;
    const advisor = this.controller.customer.application.primaryPolicy.primaryPolicyAgent.primaryAgent.person;
    const currentTimeStamp = Utils.tzDate(new Date(), 'America/New_York').format('YYYY-MM-DD HH:mm:ss');

    return `<tr>Failure date and timestamp: ${currentTimeStamp} EST</tr> <tr>Environment: ${currentEnvironment}</tr> <tr>Client Name: ${
      customer.firstName
    } ${customer.lastName}</tr> <tr>Client Email: ${
      customer.email
    } </tr> <tr>Advisor Name: ${advisor.getFullName()}</tr> <tr>Advisor Email: ${advisor.email}</tr>`;
  }

  @remote({ on: 'server' })
  sendEmailNotification(emailTemplateKey: string, userEmail: string): Promise<string | void> {
    if (this.emailTemplates.length === 0) {
      this.initializeEmailTemplates();
    }

    const matchingTemplate: EmailTemplate = this.emailTemplates.find(
      (template) => template.templateName === emailTemplateKey
    );

    if (matchingTemplate) {
      const email = this.amorphic.config.nconf.get(emailTemplateKey);
      const environment = this.amorphic.config.nconf.get('environment');
      const customerInfo = this.createInfoTable(environment);

      const sendData = {
        slug: 'general-email',
        compiledHTML: `${matchingTemplate.emailBody} </br> ${customerInfo}`,
        subject: `[${environment}] ${matchingTemplate.emailSubject}`,
        fromEmail: matchingTemplate.fromEmail,
        fromName: matchingTemplate.fromName,
        recipientEmails: email.split(';'),
        bccAddresses: [],
      };

      return Promise.resolve(sendData)
        .then((data) => HitAppCtx.get.emailer.sendToEmailService(data))
        .then(() => 'emailSent')
        .catch((error) => {
          LoggerSingleton.getInstance().error(
            {
              module: 'EmailNotificationController',
              function: 'sendEmailNotification',
              category: Category.request,
              context: {
                piiLevel: PiiLevelEnum.nonPII,
              },
              error: error,
            },
            'Unable to send support email'
          );
        });
    } else {
      LoggerSingleton.getInstance().error(
        {
          module: 'EmailNotificationController',
          function: 'sendEmailNotification',
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.nonPII,
          },
          error: {
            isHumanRelated: false,
          },
        },
        'Unable to send support email, no matching email template was found.'
      );
    }
  }
}
